import * as React from 'react';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import {Button} from 'shells/button/button';
import {EmptyStateLarge} from 'shells/empty-states/empty-state-large';
import {Illustration} from 'shells/illustrations/illustration';
import {LoadingPage} from 'shells/loading-page/loading-page';

import {useDismissibleUserSetting} from '../../../util/hooks/use-dismissible-user-setting';

import {useGetInboundSmsRules} from '../../inbox/sms/graphql/hooks/use-get-inbound-sms-rules';
import {InboundSmsSettingsWrapper} from '../../inbox/sms/inbound/inbound-sms-settings-wrapper';
import {RouteContext} from '../../route-context';

import {SettingsSidebar} from '../settings-sidebar';

import {useGetInboxStatus} from './graphql/hooks';
import {getIsEnrolled, getIsEnrollmentFormNotStarted} from './helpers';
import {EnrollmentForm} from './pages/enrollment/enrollment-form';
import {Home} from './pages/home/home';
import {PhoneNumber} from './pages/phone-number/phone-number';

import styles from './text-messaging-page.module.css';

// My twin lives in library/Nut/Settings/User.php
const HAS_DISMISSED_ONBOARDING_SETTING = 'has_dismissed_text_messaging_onboarding';

type Props = {
    startedFrom: 'marketing' | 'engagement';
} & ContextRouter;

export function TextMessagingPage(props: Props) {
    const selectedTab = props.match.params.id;

    const isEmbedded = React.useContext(RouteContext).isEmbedded;
    const {inboxStatus, isLoading: isLoadingInboxStatus} = useGetInboxStatus();
    const {inboundSmsRules, isLoading: isLoadingSmsRules, refetch} = useGetInboundSmsRules();

    const {
        shouldShow,
        onDismiss: dismissOnboarding,
        isLoading: isDismissingOnboarding,
    } = useDismissibleUserSetting(HAS_DISMISSED_ONBOARDING_SETTING);

    const EmptyState = (
        <EmptyStateLarge
            icon={
                isEmbedded ? (
                    <Illustration src='phone-setup-embedded' width={270} />
                ) : (
                    <Illustration src='phone-setup' width={270} />
                )
            }
            title={`${isEmbedded ? 'Inbox FX' : ' Nutshell'} + SMS messaging`}
            detailTextBullets={[
                `Send and receive SMS messages via ${
                    isEmbedded ? 'Inbox FX' : 'the Nutshell inbox'
                }`,
                'Manage 1-1 conversations with prospects, or engage in mass outreach with SMS Marketing',
                'Automatically create leads for new inbound SMS messages',
            ]}
            cta={
                <Button size='big' variant='primary' onClick={dismissOnboarding}>
                    Begin registration
                </Button>
            }
        />
    );

    const isEnrollmentFormNotStarted = getIsEnrollmentFormNotStarted(inboxStatus);
    const shouldShowOnboarding = shouldShow && isEnrollmentFormNotStarted;
    const getContent = (tab?: string | null) => {
        switch (tab) {
            case 'main':
                return <Home startedFrom={props.startedFrom} />;
            case 'enroll':
                return <EnrollmentForm />;
            case 'phone-number':
                return <PhoneNumber startedFrom={props.startedFrom} />;
            default:
                return shouldShowOnboarding ? EmptyState : <Home startedFrom={props.startedFrom} />;
        }
    };

    const isEnrolled = getIsEnrolled(inboxStatus);
    const shouldLoadInboundSmsSettings =
        props.startedFrom === 'engagement' && isEnrolled && !shouldShowOnboarding;

    return (
        <div className='flex flex-dir-col flex-grow'>
            {isLoadingInboxStatus || (shouldLoadInboundSmsSettings && isLoadingSmsRules) ? (
                isDismissingOnboarding || <LoadingPage />
            ) : (
                <div className={isEnrolled ? styles['container-no-sidebar'] : styles.container}>
                    <div className='flex flex-dir-col gap-16'>
                        {getContent(selectedTab)}
                        {props.startedFrom === 'engagement' &&
                            !shouldShowOnboarding &&
                            !selectedTab && (
                                <InboundSmsSettingsWrapper
                                    inboxStatus={inboxStatus}
                                    inboundSmsRules={inboundSmsRules}
                                    refetch={refetch}
                                />
                            )}
                    </div>
                    {!isEnrolled && <SettingsSidebar glossaryKey='/inbox/settings/sms' />}
                </div>
            )}
        </div>
    );
}
